import { getDojoActivityStatus } from './dojoActivityStatus';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { calculateFoodNutrients } from '../../utils/Nutrition';
import { useForegroundInterval } from '../../services/AppStateService';
import { getTravelReplacementConsistencyOverride } from '../../utils/travelReplacementConsistency';
// Preserve the personal snapshot fallback when a summary has not arrived yet.
const computeMealNutritionFallback = (activity) => {
  if (activity?.type !== 'meal') return null;
  const foods = activity?.data?.foods;
  if (!Array.isArray(foods) || foods.length === 0) return null;
  const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, water_ml: 0 };
  for (const f of foods) {
    try {
      const n = calculateFoodNutrients(f, null);
      totals.calories += n.calories || 0;
      totals.protein += n.protein || 0;
      totals.carbs += n.carbs || 0;
      totals.fat += n.fat || 0;
      totals.water_ml += n.water_ml || 0;
    } catch (e) {
      // Skip foods that can't be computed; partial totals are still
      // better than hiding the pill entirely.
    }
  }
  return totals;
};

// Small inline countdown for todo rows
const InlineCountdown = React.memo(function InlineCountdown({ dueAt }) {
  const [label, setLabel] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);

  const compute = useCallback(() => {
    if (!dueAt) {
      setLabel('');
      return;
    }
    const now = Date.now();
    const dueTime = new Date(dueAt).getTime();
    const remaining = dueTime - now;

    if (remaining <= 0) {
      setLabel('');
      setIsUrgent(false);
      return;
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    setIsUrgent(hours === 0 && minutes < 30);

    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    if (hours > 0) {
      setLabel(`${hours}h ${minutes}m ${seconds}s`);
    } else if (minutes > 0) {
      setLabel(`${minutes}m ${seconds}s`);
    } else {
      setLabel(`${seconds}s`);
    }
  }, [dueAt]);

  useEffect(() => {
    compute();
  }, [compute]);

  useForegroundInterval(compute, dueAt ? 1000 : null);

  if (!label) return null;
  return <span className={`todo-countdown ${isUrgent ? 'urgent' : ''}`}>{label}</span>;
});

// Calculate consistency points breakdown for an activity
// Returns: { earned, pr, pending, missed } where all sum to plannedCount
const getConsistencyPoints = (activity) => {
  const travelOverride = getTravelReplacementConsistencyOverride(activity);
  if (travelOverride.handled) return travelOverride.value;
  if (!activity.due_at) return null; // No points for unscheduled
  // Rest-day workouts are excluded from consistency entirely (no pill at all).
  if (activity.status === 'rest') return null;

  const now = Date.now();
  const dueTime = new Date(activity.due_at).getTime();
  const isExpired = dueTime < now && activity.status !== 'complete';
  const isComplete = activity.status === 'complete';

  // Helper to check if exercise is complete
  const exerciseIsComplete = (exercise) => {
    if (!exercise || !Array.isArray(exercise.sets) || exercise.sets.length === 0) {
      return false;
    }
    return exercise.sets.every((set) => set && set.complete);
  };

  // Build key for matching planned vs actual exercises
  const buildExerciseKey = (exercise, idx) => {
    if (!exercise) return `idx:${idx}`;
    if (exercise.exercise_id) return `ex:${exercise.exercise_id}`;
    if (exercise._id) return String(exercise._id);
    if (exercise.name) return `name:${exercise.name}`;
    return `idx:${idx}`;
  };

  // For non-workout activities, simple logic
  if (activity.type !== 'workout') {
    if (isComplete) {
      return { earned: 1, pr: 0, pending: 0, missed: 0 };
    } else if (isExpired) {
      return { earned: 0, pr: 0, pending: 0, missed: 1 };
    } else {
      return { earned: 0, pr: 0, pending: 1, missed: 0 };
    }
  }
  // For workouts, calculate per-exercise breakdown
  const plannedExercisesRaw = activity?.source?.activity_snapshot?.data?.exercises;
  const plannedExercises = Array.isArray(plannedExercisesRaw) && plannedExercisesRaw.length > 0
    ? plannedExercisesRaw
    : (activity?.data?.exercises || []);
  const actualExercises = activity?.data?.exercises || [];
  const plannedCount = plannedExercises.length || 1;

  // Build map of completed exercises with their PR status
  const completedMap = new Map(); // key -> { count, prCount }
  actualExercises.forEach((ex, idx) => {
    if (exerciseIsComplete(ex)) {
      const key = buildExerciseKey(ex, idx);
      const existing = completedMap.get(key) || { count: 0, prCount: 0 };
      existing.count += 1;
      // Check if this exercise was a PR (if the data is available)
      if (ex.isPR || ex.is_pr) {
        existing.prCount += 1;
      }
      completedMap.set(key, existing);
    }
  });

  // Match planned exercises against completed
  let earned = 0;
  let pr = 0;
  let missed = 0;

  plannedExercises.forEach((plannedEx, idx) => {
    const key = buildExerciseKey(plannedEx, idx);
    const completed = completedMap.get(key);

    if (completed && completed.count > 0) {
      // This planned exercise was completed
      if (completed.prCount > 0) {
        pr += 1;
        completed.prCount -= 1;
      } else {
        earned += 1;
      }
      completed.count -= 1;
    } else if (isExpired) {
      // Not completed and time has passed
      missed += 1;
    }
    // If not completed and not expired, it will be counted as pending
  });

  // Pending = planned but not yet completed (only if not expired)
  const pending = isExpired ? 0 : (plannedCount - earned - pr);

  return { earned, pr, pending, missed };
};

// Consistency points pills component - renders multiple pills for breakdown with tooltip
const PointsPill = ({ points, passive = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const wrapperRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  if (!points) return null;

  const { earned, pr, pending, missed } = points;
  const pills = [];

  // Gold PRs first (most prestigious)
  if (pr > 0) {
    pills.push(<span key="pr" className="points-pill pr">+{pr}</span>);
  }

  // Green earned (completed, non-PR)
  if (earned > 0) {
    pills.push(<span key="earned" className="points-pill earned">+{earned}</span>);
  }

  // Gray pending
  if (pending > 0) {
    pills.push(<span key="pending" className="points-pill pending">{pending}</span>);
  }

  // Red missed
  if (missed > 0) {
    pills.push(<span key="missed" className="points-pill missed">-{missed}</span>);
  }

  if (pills.length === 0) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  const total = earned + pr + pending + missed;

  return (
    <span className="points-pills-wrapper" ref={wrapperRef}>
      <span className="points-pills" onClick={passive ? undefined : handleClick}>{pills}</span>
      {showTooltip && (
        <div className="points-tooltip" onClick={(e) => e.stopPropagation()}>
          <div className="points-tooltip-title">Consistency Points</div>
          <div className="points-tooltip-desc">
            Points earned by completing planned activities on time.
          </div>
          <div className="points-tooltip-breakdown">
            {pr > 0 && <div className="points-tooltip-row pr"><span>PRs</span><span>+{pr}</span></div>}
            {earned > 0 && <div className="points-tooltip-row earned"><span>Earned</span><span>+{earned}</span></div>}
            {pending > 0 && <div className="points-tooltip-row pending"><span>Pending</span><span>{pending}</span></div>}
            {missed > 0 && <div className="points-tooltip-row missed"><span>Missed</span><span>-{missed}</span></div>}
            <div className="points-tooltip-row total"><span>Total planned</span><span>{total}</span></div>
          </div>
          <button className="points-tooltip-close" onClick={() => setShowTooltip(false)}>Got it</button>
        </div>
      )}
    </span>
  );
};


export function ActivityStatusBullet({ activity }) {
  const presentation = getDojoActivityStatus(activity);
  const symbol = presentation.status === 'rest' ? (activity.data?.rest_reason === 'sick' ? '🛌' : '🌴') : presentation.symbol;
  return <span className={`todo-bullet ${presentation.status === 'live' ? 'todo-bullet-live' : ''}`} aria-hidden="true">
    {presentation.status === 'live' ? <span className="todo-live-dot" /> : symbol}
  </span>;
}

export const isDefaultExpanded = activity => !['complete', 'completed', 'rest'].includes(activity?.status);

// Sparse social workout feeds omit exercises. Do not invent planned points.
export function ActivityHeaderSummary({ activity, hideNutrition = false, peer = false }) {
  const normalized = activity.status === 'completed' ? { ...activity, status: 'complete' } : activity;
  let nutrition = activity.type === 'meal' && !activity.nutrition_summary_unavailable ? activity.nutrition_summary : null;
  if (!hideNutrition && activity.type === 'meal' && !activity.nutrition_summary_unavailable && (!nutrition || (!(nutrition.calories > 0) && !(nutrition.protein > 0)))) {
    nutrition = computeMealNutritionFallback(activity) || nutrition;
  }
  const score = activity.type === 'workout' ? activity.score_summary?.total_score : null;
  // Older responses may not yet include score_summary; retain their existing
  // volume display without presenting that physical quantity as a score.
  const hasScore = typeof score === 'number' && Number.isFinite(score) && score >= 0;
  const volume = activity.type === 'workout' ? activity.volume_summary?.total_volume : null;
  const sparsePeerWorkout = peer && activity.type === 'workout' && !Array.isArray(activity.data?.exercises);
  // Full detail (and owner rows) always uses current exercises, never a stale
  // summary inherited from the sparse feed during disclosure expansion.
  const summary = activity.consistency_summary;
  const validSummary = summary && ['earned', 'pr', 'pending', 'missed']
    .every(key => Number.isSafeInteger(summary[key]) && summary[key] >= 0);
  const eligibleSummary = activity.source?.compliance_role !== 'travel_replacement'
    && activity.status !== 'rest' && (activity.status === 'replaced' || activity.due_at);
  // Retained feed data must cross the same deadline boundary as owner rows on
  // rerender, without a detail request, polling, or mutating the saved summary.
  const summaryExpired = !['complete', 'completed', 'replaced'].includes(activity.status)
    && new Date(activity.due_at).getTime() < Date.now();
  const currentSummary = validSummary && summaryExpired
    ? { ...summary, pending: 0, missed: summary.missed + summary.pending }
    : summary;
  const points = sparsePeerWorkout
    ? (eligibleSummary && validSummary ? currentSummary : null)
    : getConsistencyPoints(normalized);
  const showCountdown = !['complete', 'completed', 'rest'].includes(activity.status) && activity.due_at && new Date(activity.due_at).getTime() > Date.now();
  return <>
    {!hideNutrition && nutrition && (nutrition.calories > 0 || nutrition.protein > 0) && <span className="todo-meal-macros">
      {nutrition.calories > 0 && <span className="macro-cal">+{Math.round(nutrition.calories)}</span>}
      {nutrition.protein > 0 && <span className="macro-pro">+{Math.round(nutrition.protein)}g</span>}
    </span>}
    {hasScore ? <span className="todo-workout-volume" title="Workout score"><span className="volume-val">+{Math.round(score).toLocaleString('en-US')}</span></span>
      : volume > 0 && <span className="todo-workout-volume" title="Workout volume"><span className="volume-val">+{Math.round(volume).toLocaleString()}</span></span>}
    <PointsPill points={points} passive={peer} />
    {showCountdown && <InlineCountdown dueAt={activity.due_at} />}
  </>;
}
